// (c) chaehongee. Confidential and proprietary.

import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'
import type { RootState } from '../store'
import { listLoras } from '../../services/loras'
import type { LoraResponse } from 'models-ts/lora'

export interface LorasState {
    items: LoraResponse[]
    selectedId: string | null
    loading: boolean
    refreshing: boolean
    error: string | null
}

const initialState: LorasState = {
    items: [],
    selectedId: null,
    loading: false,
    refreshing: false,
    error: null,
}

export const fetchIdentityLoras = createAsyncThunk(
    'loras/fetchIdentity',
    async () => {
        const data = await listLoras()
        return data
    },
    {
        condition: (_, { getState }) => {
            const state = getState() as { loras: LorasState }
            // Skip if already loading/refreshing to prevent duplicate requests
            return !state.loras.loading && !state.loras.refreshing
        },
    }
)

const lorasSlice = createSlice({
    name: 'loras',
    initialState,
    reducers: {
        selectLora(state, action: PayloadAction<string | null>) {
            state.selectedId = action.payload
        },
        addPlaceholderLora(state, action: PayloadAction<{ id: string; name: string }>) {
            const placeholder: LoraResponse = {
                id: action.payload.id,
                user_id: null,
                name: action.payload.name,
                category: 'identity',
                thumbnail_url: null,
                replicate_model: null,
                training_status: 'processing',
                privacy: 'private',
                shared_with: [],
                usage_count: 0,
            }
            state.items.unshift(placeholder)
        },
        removePlaceholderLora(state, action: PayloadAction<string>) {
            state.items = state.items.filter((item) => item.id !== action.payload)
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchIdentityLoras.pending, (state) => {
                // If we have cached items, this is a background refresh
                if (state.items.length > 0) {
                    state.refreshing = true
                } else {
                    // First load - show loading state
                    state.loading = true
                }
                state.error = null
            })
            .addCase(fetchIdentityLoras.fulfilled, (state, action) => {
                state.loading = false
                state.refreshing = false
                state.items = action.payload
            })
            .addCase(fetchIdentityLoras.rejected, (state, action) => {
                state.loading = false
                state.refreshing = false
                state.error = action.error.message || 'Failed to load identities'
            })
    },
})

export const { selectLora, addPlaceholderLora, removePlaceholderLora } = lorasSlice.actions

export const selectLorasState = (s: RootState) => s.loras

export default lorasSlice.reducer


